import React, { useMemo } from "react";
import { Table } from "react-bootstrap";
import { getBillTaxBreakdownRows, toMoneyNumber } from "../../../../utils/financeEngine";
import { formatAmount } from "../../../PatientAccounts/utils/patientAccountUtils";

const usePharmacyRowModel = ({
	cart = [],
	itemTaxes = {},
	totals = {},
	posTaxType,
	isSameState,
}) => {
	const billDiscountRate = useMemo(() => {
		const totalTaxableBeforeDiscount = cart.reduce((sum, item) => {
			const itemKey = `${item._id}-${item.selectedBatchId}`;
			const taxInfo = itemTaxes[itemKey];
			return sum + (taxInfo?.taxableAmount || 0);
		}, 0);
		const billDiscount = totals?.discount || 0;
		return totalTaxableBeforeDiscount > 0 ? billDiscount / totalTaxableBeforeDiscount : 0;
	}, [cart, itemTaxes, totals?.discount]);

	const rows = useMemo(
		() =>
			cart.map((item) => {
				const itemKey = `${item._id}-${item.selectedBatchId}`;
				const taxInfo = itemTaxes[itemKey] || {};
				const taxRate = taxInfo.taxPercentage || 0;
				const taxableBeforeDiscount = taxInfo.taxableAmount || 0;
				const taxableAfterDiscount =
					taxableBeforeDiscount - taxableBeforeDiscount * billDiscountRate;

				let rowTax = 0;
				let rowCGST = 0;
				let rowSGST = 0;
				let rowIGST = 0;
				let rowVAT = 0;

				if (posTaxType === "gst") {
					if (isSameState) {
						rowTax = (taxableAfterDiscount * taxRate) / 100;
						rowCGST = rowTax / 2;
						rowSGST = rowTax / 2;
					} else {
						rowTax = (taxableAfterDiscount * taxRate) / 100;
						rowIGST = rowTax;
					}
				} else if (posTaxType === "vat") {
					rowVAT = (taxableAfterDiscount * taxRate) / 100;
					rowTax = rowVAT;
				}

				const grossTotal = taxableAfterDiscount + rowTax;

				return {
					itemKey,
					name: item.brandName || item.saltName,
					qty: item.quantity || 0,
					mrp: item.salePrice || 0,
					taxRate,
					taxableAfterDiscount,
					rowCGST,
					rowSGST,
					rowIGST,
					rowVAT,
					rowTax,
					grossTotal,
				};
			}),
		[cart, itemTaxes, billDiscountRate, isSameState, posTaxType]
	);

	const footerTotals = useMemo(
		() =>
			rows.reduce(
				(acc, row) => ({
					taxable: acc.taxable + row.taxableAfterDiscount,
					CGST: acc.CGST + row.rowCGST,
					SGST: acc.SGST + row.rowSGST,
					IGST: acc.IGST + row.rowIGST,
					VAT: acc.VAT + row.rowVAT,
					tax: acc.tax + row.rowTax,
					gross: acc.gross + row.grossTotal,
				}),
				{ taxable: 0, CGST: 0, SGST: 0, IGST: 0, VAT: 0, tax: 0, gross: 0 }
			),
		[rows]
	);

	return { rows, footerTotals, billDiscountRate };
};

const useDisplayAmount = (displayCurrency, pharmacyCurrency, convertCurrency) =>
	useMemo(
		() => (amount) => {
			const value = toMoneyNumber(amount);
			if (displayCurrency === pharmacyCurrency) return value;
			return toMoneyNumber(convertCurrency(value, pharmacyCurrency, displayCurrency));
		},
		[convertCurrency, displayCurrency, pharmacyCurrency]
	);

export const BillingPharmacyMedicinesTable = ({
	t,
	cart = [],
	priorPharmacyItems = [],
	itemTaxes = {},
	totals = {},
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	displayCurrency,
	pharmacyCurrency,
	convertCurrency,
	getCurrencySymbol,
	showPosTax = true,
}) => {
	const isSameState =
		!patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

	const { rows, footerTotals } = usePharmacyRowModel({
		cart,
		itemTaxes,
		totals,
		posTaxType,
		isSameState,
	});

	const priorRows = useMemo(
		() =>
			(priorPharmacyItems || []).map((item, index) => ({
				itemKey: `prior-${item.item_id || index}-${item.stock_id || index}`,
				name: item.item || item.brand_name || item.medicine_name || "-",
				qty: item.qty || 0,
				mrp: item.unit_price || 0,
				taxRate: item.tax_percentage || item.tax || 0,
				taxableAfterDiscount: item.sub_total || 0,
				rowVAT: item.tax_percentage || item.tax ? toMoneyNumber(item.tax) : 0,
				rowTax: toMoneyNumber(item.tax),
				rowCGST: 0,
				rowSGST: 0,
				rowIGST: 0,
				grossTotal: item.net_total || item.amount || 0,
				date: item.date || "",
				isPrior: true,
			})),
		[priorPharmacyItems]
	);

	const toDisplay = useDisplayAmount(displayCurrency, pharmacyCurrency, convertCurrency);
	const money = (amount) => formatAmount(toDisplay(amount), displayCurrency, getCurrencySymbol);

	const taxColCount = showPosTax ? (posTaxType === "gst" ? (isSameState ? 2 : 1) : 1) : 0;
	const emptyColSpan = 3 + taxColCount + (showPosTax ? 2 : 0) + 1;
	const hasRows = priorRows.length > 0 || cart.length > 0;

	const combinedFooter = useMemo(() => {
		const base = { ...footerTotals };
		priorRows.forEach((row) => {
			base.taxable += row.taxableAfterDiscount;
			base.tax += row.rowTax;
			base.VAT += row.rowVAT || 0;
			base.gross += row.grossTotal;
		});
		return base;
	}, [footerTotals, priorRows]);

	if (!hasRows) {
		return (
			<div className="table-responsive">
				<Table bordered size="sm" className="mb-0 billing-table medicines-table billing-pharmacy-table">
					<tbody>
						<tr>
							<td colSpan={emptyColSpan} className="text-center text-muted py-3">
								{t("No medicines in cart")}
							</td>
						</tr>
					</tbody>
				</Table>
			</div>
		);
	}

	const renderDataRows = (list) =>
		list.map((row) => (
			<tr
				key={row.itemKey}
				className={row.isPrior ? "billing-row billing-row--locked" : undefined}
			>
				<td>
					<div className="billing-line-name">
						<span className="billing-line-title">{row.name}</span>
						{row.isPrior && (
							<span className="billing-source-badge billing-source-badge--prescribed ms-1">
								{row.date || t("Previous")}
							</span>
						)}
					</div>
				</td>
				<td className="text-center">{row.qty}</td>
				<td className="text-end">{money(row.mrp)}</td>
				{showPosTax && (
					<>
						<td className="text-center">{row.taxRate}%</td>
						<td className="text-end">{money(row.taxableAfterDiscount)}</td>
						{posTaxType === "gst" ? (
							isSameState ? (
								<>
									<td className="text-end">{money(row.rowCGST)}</td>
									<td className="text-end">{money(row.rowSGST)}</td>
								</>
							) : (
								<td className="text-end">{money(row.rowIGST)}</td>
							)
						) : (
							<td className="text-end">{money(row.rowVAT || row.rowTax)}</td>
						)}
					</>
				)}
				<td className="text-end fw-semibold">{money(row.grossTotal)}</td>
			</tr>
		));

	return (
		<div className="table-responsive">
			<Table bordered size="sm" className="mb-0 billing-table medicines-table billing-pharmacy-table">
				<thead>
					<tr>
						<th scope="col">{t("Item")}</th>
						<th scope="col" className="text-center billing-col-qty">
							{t("Qty")}
						</th>
						<th scope="col" className="text-center billing-col-mrp">
							{t("Price (MRP)")}
						</th>
						{showPosTax && (
							<>
								<th scope="col" className="text-center billing-col-tax-pct">
									{t("Tax %")}
								</th>
								<th scope="col" className="text-center">
									{t("Taxable Value")}
								</th>
								{posTaxType === "gst" ? (
									isSameState ? (
										<>
											<th scope="col" className="text-center">{t("CGST")}</th>
											<th scope="col" className="text-center">{t("SGST")}</th>
										</>
									) : (
										<th scope="col" className="text-center">{t("IGST")}</th>
									)
								) : (
									<th scope="col" className="text-center">{t("VAT")}</th>
								)}
							</>
						)}
						<th scope="col" className="text-end">
							{t("Amount")}
						</th>
					</tr>
				</thead>
				<tbody>
					{renderDataRows(priorRows)}
					{renderDataRows(rows)}
				</tbody>
				{(priorRows.length > 0 || rows.length > 0) && (
					<tfoot>
						<tr>
							<td colSpan={showPosTax ? 4 : 2} className="text-end fw-semibold">
								{t("Total")}
							</td>
							{showPosTax && (
								<td className="text-end fw-semibold">{money(combinedFooter.taxable)}</td>
							)}
							{showPosTax &&
								(posTaxType === "gst" ? (
									isSameState ? (
										<>
											<td className="text-end fw-semibold">{money(combinedFooter.CGST)}</td>
											<td className="text-end fw-semibold">{money(combinedFooter.SGST)}</td>
										</>
									) : (
										<td className="text-end fw-semibold">{money(combinedFooter.IGST)}</td>
									)
								) : (
									<td className="text-end fw-semibold">{money(combinedFooter.VAT)}</td>
								))}
							<td className="text-end fw-semibold">{money(combinedFooter.gross)}</td>
						</tr>
					</tfoot>
				)}
			</Table>
		</div>
	);
};

export const BillingPharmacyTaxBreakdown = ({
	t,
	cart = [],
	itemTaxes = {},
	totals = {},
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	displayCurrency,
	pharmacyCurrency,
	convertCurrency,
	getCurrencySymbol,
	showPosTax = true,
}) => {
	const isSameState =
		!patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

	const toDisplay = useDisplayAmount(displayCurrency, pharmacyCurrency, convertCurrency);
	const money = (amount) => formatAmount(toDisplay(amount), displayCurrency, getCurrencySymbol);

	const taxBreakdown = useMemo(() => {
		if (!cart.length || !itemTaxes) return [];
		return getBillTaxBreakdownRows(cart, itemTaxes, totals, posTaxType, isSameState, "sale");
	}, [cart, itemTaxes, totals, posTaxType, isSameState]);

	const totalTax = toDisplay(totals?.tax || 0);

	if (
		!showPosTax ||
		!cart.length ||
		taxBreakdown.length === 0 ||
		totalTax <= 0
	) {
		return null;
	}

	return (
		<div className="billing-pharmacy-tax-table-wrap mt-3">
			<div className="billing-pharmacy-tax-table-title">{t("Tax Breakdown")}</div>
			<div className="table-responsive">
				<Table bordered size="sm" className="mb-0 billing-table billing-pharmacy-tax-table">
					<thead>
						<tr>
							<th className="text-center">{t("Tax %")}</th>
							<th className="text-end">{t("Taxable")}</th>
							{posTaxType === "gst" ? (
								isSameState ? (
									<>
										<th className="text-end">{t("CGST")}</th>
										<th className="text-end">{t("SGST")}</th>
									</>
								) : (
									<th className="text-end">{t("IGST")}</th>
								)
							) : (
								<th className="text-end">{t("VAT")}</th>
							)}
							<th className="text-end">{t("Total")}</th>
						</tr>
					</thead>
					<tbody>
						{taxBreakdown.map((group, index) => (
							<tr key={index}>
								<td className="text-center">{group.taxRate}%</td>
								<td className="text-end">{money(group.netTaxableAmount)}</td>
								{posTaxType === "gst" ? (
									isSameState ? (
										<>
											<td className="text-end billing-tax-cell">{money(group.CGST)}</td>
											<td className="text-end billing-tax-cell">{money(group.SGST)}</td>
										</>
									) : (
										<td className="text-end billing-tax-cell">{money(group.IGST)}</td>
									)
								) : (
									<td className="text-end billing-tax-cell">{money(group.VAT)}</td>
								)}
								<td className="text-end fw-semibold">{money(group.totalAmount)}</td>
							</tr>
						))}
					</tbody>
				</Table>
			</div>
		</div>
	);
};

const getDiscountLabel = (t, discountType, discountValue, discount, totalTaxableBeforeDiscount) => {
	if (discountType === "Percentage" && Number(discountValue) > 0) {
		return `${t("Discount")} (${Number(discountValue)}%)`;
	}
	if (discount > 0 && totalTaxableBeforeDiscount > 0) {
		const effectivePct = ((discount / totalTaxableBeforeDiscount) * 100).toFixed(2);
		return `${t("Discount")} (${effectivePct}%)`;
	}
	return t("Discount");
};

export const BillingPharmacySummaryBreakdown = ({
	t,
	cart = [],
	totals = {},
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	discountType = "Percentage",
	discountValue = 0,
	displayCurrency,
	pharmacyCurrency,
	convertCurrency,
	getCurrencySymbol,
	isOpen,
	onToggle,
	showPosTax = true,
}) => {
	const isSameState =
		!patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

	const toDisplay = useDisplayAmount(displayCurrency, pharmacyCurrency, convertCurrency);
	const money = (amount) => formatAmount(toDisplay(amount), displayCurrency, getCurrencySymbol);

	const totalTaxableBeforeDiscount = toDisplay(totals?.subtotal || 0);
	const discount = toDisplay(totals?.discount || 0);
	const netTaxable = toMoneyNumber(totalTaxableBeforeDiscount - discount);
	const totalCGST = toDisplay(totals?.CGST || 0);
	const totalSGST = toDisplay(totals?.SGST || 0);
	const totalIGST = toDisplay(totals?.IGST || 0);
	const totalVAT = toDisplay(totals?.VAT || 0);
	const totalTax = toDisplay(totals?.tax || 0);
	const transport = toDisplay(totals?.transport || 0);
	const pharmacyGrandTotal = toDisplay(totals?.netTotal || 0);
	const discountLabel = getDiscountLabel(
		t,
		discountType,
		discountValue,
		discount,
		totalTaxableBeforeDiscount
	);

	const hasPharmacyItems = cart.length > 0;

	return (
		<div className="billing-pharmacy-summary-block mb-3">
			<button
				type="button"
				className="billing-pharmacy-summary-toggle"
				onClick={onToggle}
				aria-expanded={isOpen}
			>
				<span className="fw-semibold">{t("Pharmacy Medicines")}</span>
				<span className="billing-pharmacy-summary-toggle-meta">
					<span className="billing-pharmacy-summary-toggle-total">
						{money(pharmacyGrandTotal)}
					</span>
					<i className={`fa fa-chevron-${isOpen ? "up" : "down"}`} />
				</span>
			</button>

			{isOpen && hasPharmacyItems && (
				<div className="billing-pharmacy-summary-body">
					<div className="summary-row">
						<span>{showPosTax ? t("Total Taxable Value") : t("Subtotal")}</span>
						<strong>{money(totalTaxableBeforeDiscount)}</strong>
					</div>
					{discount > 0 && (
						<>
							<div className="summary-row">
								<span>{discountLabel}</span>
								<strong className="text-danger">-{money(discount)}</strong>
							</div>
							<div className="summary-row">
								<span>{showPosTax ? t("Net Taxable Value") : t("Net Amount")}</span>
								<strong>{formatAmount(netTaxable, displayCurrency, getCurrencySymbol)}</strong>
							</div>
						</>
					)}
					{showPosTax && posTaxType === "gst" && isSameState && totalCGST > 0 && (
						<div className="summary-row">
							<span>{t("Add")}: CGST</span>
							<strong className="billing-tax-cell">+{money(totalCGST)}</strong>
						</div>
					)}
					{showPosTax && posTaxType === "gst" && isSameState && totalSGST > 0 && (
						<div className="summary-row">
							<span>{t("Add")}: SGST</span>
							<strong className="billing-tax-cell">+{money(totalSGST)}</strong>
						</div>
					)}
					{showPosTax && posTaxType === "gst" && !isSameState && totalIGST > 0 && (
						<div className="summary-row">
							<span>{t("Add")}: IGST</span>
							<strong className="billing-tax-cell">+{money(totalIGST)}</strong>
						</div>
					)}
					{showPosTax && posTaxType === "vat" && totalVAT > 0 && (
						<div className="summary-row">
							<span>{t("Add")}: VAT</span>
							<strong className="billing-tax-cell">+{money(totalVAT)}</strong>
						</div>
					)}
					{showPosTax && totalTax > 0 && (
						<div className="summary-row billing-pharmacy-tax-total">
							<span>{t("Total Tax")}</span>
							<strong className="billing-tax-cell">{money(totalTax)}</strong>
						</div>
					)}
					{transport > 0 && (
						<div className="summary-row">
							<span>{t("Transport")}</span>
							<strong>+{money(transport)}</strong>
						</div>
					)}
					<div className="summary-row billing-pharmacy-grand-row">
						<span>{t("Pharmacy Grand Total")}</span>
						<strong>{money(pharmacyGrandTotal)}</strong>
					</div>
				</div>
			)}

			{isOpen && !hasPharmacyItems && (
				<div className="billing-pharmacy-summary-body text-muted small">
					{t("No medicines in cart")}
				</div>
			)}
		</div>
	);
};
